// Status utility functions for task status display
// CREATED: Centralized status color and label functions to ensure UI consistency
// Used by: DayTimeline, TaskBlock, MiniTimeline
// UPDATED: Added badge-specific functions for Team View with service-colored blocks
// CHANGE: All exported functions now use TaskStatus instead of string to enforce type safety

export type TaskStatus = 'not_started' | 'completed' | 'overdue';
export type StatusIcon = 'check' | 'clock' | 'alert';

export interface StatusStyle {
  background: string;
  border: string;
  text: string;
  dot: string;
}

const STATUS_STYLES: Record<TaskStatus, StatusStyle> = {
  completed: {
    background: 'bg-emerald-500',
    border: 'border-emerald-600',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500'
  },
  overdue: {
    background: 'bg-red-500',
    border: 'border-red-600',
    text: 'text-red-700',
    dot: 'bg-red-500'
  },
  not_started: {
    background: 'bg-amber-500',
    border: 'border-amber-600',
    text: 'text-amber-700',
    dot: 'bg-amber-500'
  }
};

const STATUS_LABELS: Record<TaskStatus, string> = {
  completed: 'Terminée',
  overdue: 'En retard',
  not_started: 'Non démarrée'
};

export function getStatusColor(status: TaskStatus = 'not_started'): string {
  const style = STATUS_STYLES[status] || STATUS_STYLES.not_started;
  return `${style.background} ${style.border}`;
}

export function getStatusLabel(status: TaskStatus = 'not_started'): string {
  return STATUS_LABELS[status] || STATUS_LABELS.not_started;
}

export function getStatusStyle(status: TaskStatus = 'not_started'): StatusStyle {
  return STATUS_STYLES[status] || STATUS_STYLES.not_started;
}

export function getStatusBorderClass(status: TaskStatus = 'not_started'): string {
  const style = STATUS_STYLES[status] || STATUS_STYLES.not_started;
  return style.border;
}

export function getStatusBadgeClass(status: TaskStatus = 'not_started'): string {
  const style = STATUS_STYLES[status] || STATUS_STYLES.not_started;
  return style.dot;
}

export function getStatusIcon(status: TaskStatus = 'not_started'): StatusIcon {
  const icons: Record<TaskStatus, StatusIcon> = {
    completed: 'check',
    overdue: 'alert',
    not_started: 'clock'
  };
  return icons[status] || 'clock';
}
